/**
 * Analytics — dataLayer (GA4 / GTM compatible).
 *
 * Golden funnel pushes live in @template-project/storefront-shared.
 * GTM loads from gtm-bootstrap-*.js using VITE_GTM_ID.
 * Page views: GTM All Pages trigger (not pushed here).
 *
 * Funnel-specific events remain in this file.
 */

export type { EcommerceItem } from '@template-project/storefront-shared';
export {
  buildBrowserBusinessEventId,
  EVENT_TYPES,
  trackViewContent,
  trackAddToCart,
  trackAddToCartFromLines,
  trackCheckoutStarted,
  trackCheckoutStartedFromCart,
  trackPurchase,
  trackPurchaseFromOrderLines,
  trackVisitorIdentity,
} from '@template-project/storefront-shared';

// ── Funnel Events ──────────────────────────────────────────────────────────

interface FunnelEventParams {
  funnel_id?: string;
  step_id?: string;
  step_type?: string;
  step_name?: string;
  placement?: string;
  experience_type?: string;
  offer_id?: string;
  offer_name?: string;
  product_id?: string;
  variant_id?: string;
  discount_type?: string;
  discount_value?: number;
  action_type?: string;
  trigger_lifecycle?: string;
  [key: string]: string | number | boolean | undefined;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function pushEvent(eventName: string, params: FunnelEventParams): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...params,
  });
}

/** Funnel experience rendered on the page. */
export function trackFunnelExperienceShown(params: {
  funnelId: string;
  stepId: string;
  stepName?: string;
  experienceType?: string;
  placement?: string;
}): void {
  pushEvent('funnel_experience_shown', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    step_name: params.stepName,
    experience_type: params.experienceType,
    placement: params.placement,
  });
}

/** User accepted a funnel offer. */
export function trackFunnelOfferAccepted(params: {
  funnelId: string;
  stepId: string;
  offerId?: string;
  offerName?: string;
  productId?: string;
}): void {
  pushEvent('funnel_offer_accepted', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    offer_id: params.offerId,
    offer_name: params.offerName,
    product_id: params.productId,
  });
}

/** User declined a funnel offer. */
export function trackFunnelOfferDeclined(params: {
  funnelId: string;
  stepId: string;
  offerId?: string;
}): void {
  pushEvent('funnel_offer_declined', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
    offer_id: params.offerId,
  });
}

/** Product added to cart from a funnel experience. */
export function trackFunnelAddToCart(params: {
  productId: string;
  variantId?: string;
  quantity: number;
  source: 'funnel_popup' | 'funnel_offer' | 'funnel_free_gift';
}): void {
  pushEvent('funnel_add_to_cart', {
    product_id: params.productId,
    variant_id: params.variantId,
    quantity: params.quantity,
    source: params.source,
  });
}

/** Popup shown (leads, free gift, or display offer). */
export function trackFunnelPopupShown(params: {
  stepId: string;
  actionType: string;
  actionId: string;
}): void {
  pushEvent('funnel_popup_shown', {
    step_id: params.stepId,
    action_type: params.actionType,
    action_id: params.actionId,
  });
}

/** Leads popup email submitted. */
export function trackFunnelLeadsSubmitted(params: {
  stepId: string;
  actionId: string;
}): void {
  pushEvent('funnel_leads_submitted', {
    step_id: params.stepId,
    action_id: params.actionId,
  });
}

/** Funnel experience dismissed by user. */
export function trackFunnelExperienceDismissed(params: {
  funnelId: string;
  stepId: string;
}): void {
  pushEvent('funnel_experience_dismissed', {
    funnel_id: params.funnelId,
    step_id: params.stepId,
  });
}

/** Footer / sitewide newsletter signup. Pass sha256(email) only — never raw email. */
export function trackNewsletterSignup(params: { emailHash: string }): void {
  pushEvent('newsletter_signup', {
    email_hash: params.emailHash,
  });
}
